import * as React from "react"
import { AddressSelect } from "../components/AddressSelect"
import { Field, RequiredField } from "../components/Fields"
import type { CheckoutViewModel } from "../viewTypes"

type Props = Pick<CheckoutViewModel,
    "phase" | "detailsPanelRef" | "detailErrors" | "validationAttempt" | "customer" | "setCustomer" | "clearDetailError" | "quote" | "postcode" | "manualDeliveryEntry" | "addressLookupPhase" | "addressOptions" | "selectedAddressId" | "setSelectedAddressId" | "setSelectedDeliveryAddress" | "setManualDeliveryEntry" | "manualDeliveryAddress" | "setManualDeliveryAddress" | "addressLookupMessage">

export function DetailsStep(inputs: Props) {
    const {
        phase,
        detailsPanelRef,
        detailErrors,
        validationAttempt,
        customer,
        setCustomer,
        clearDetailError,
        quote,
        postcode,
        manualDeliveryEntry,
        addressLookupPhase,
        addressOptions,
        selectedAddressId,
        setSelectedAddressId,
        setSelectedDeliveryAddress,
        setManualDeliveryEntry,
        manualDeliveryAddress,
        setManualDeliveryAddress,
        addressLookupMessage,
    } = inputs
    const manualAddressId = React.useId()
    const deliveryPostcode = quote?.postcode || postcode.trim().toUpperCase()
    return (
        <section ref={detailsPanelRef} className="ah-details-panel">
            <fieldset className="ah-details-fields" disabled={["paying", "confirming"].includes(phase)}>
            <div className="ah-details-heading">
                <h2 className="ah-section-title">1. Delivery details</h2>
                <span className="ah-delivery-updates-chip"><span aria-hidden="true">💬</span>Get live delivery updates</span>
            </div>
            <RequiredField label="Email" hideLabel error={detailErrors.email} attempt={validationAttempt}>
                <input data-detail-field="email" type="email" placeholder="Your email address" value={customer.email} onChange={(event) => { setCustomer({ ...customer, email: event.target.value }); clearDetailError("email") }} autoComplete="email" inputMode="email" />
            </RequiredField>
            <RequiredField label="Phone" hideLabel error={detailErrors.phone} attempt={validationAttempt}>
                <input data-detail-field="phone" type="tel" placeholder="Your phone" value={customer.phone} onChange={(event) => { setCustomer({ ...customer, phone: event.target.value }); clearDetailError("phone") }} autoComplete="tel" inputMode="tel" />
            </RequiredField>
            <div className="ah-address-lookup">
                <AddressSelect
                    options={addressOptions}
                    value={manualDeliveryEntry ? "" : selectedAddressId}
                    loading={addressLookupPhase === "loading" || (phase === "initialising" && addressLookupPhase === "idle")}
                    disabled={["paying", "confirming"].includes(phase) || (phase === "initialising" && addressLookupPhase === "idle")}
                    hint={addressLookupPhase === "loading" ? `Finding addresses for ${deliveryPostcode}…` : undefined}
                    error={manualDeliveryEntry ? undefined : detailErrors.address}
                    attempt={validationAttempt}
                    onChange={(id) => {
                        const option = addressOptions.find((item) => item.id === id)
                        setSelectedAddressId(id)
                        setSelectedDeliveryAddress(option ? { line1: option.line1, line2: option.line2, city: option.city, state: option.state || undefined, postal_code: option.postalCode, country: option.country } : null)
                        if (option) { setManualDeliveryEntry(false); clearDetailError("address") }
                    }}
                />
                <button type="button" className="ah-address-manual-toggle" aria-expanded={manualDeliveryEntry} aria-controls={manualAddressId}
                    onClick={() => { setManualDeliveryEntry(!manualDeliveryEntry); clearDetailError("address") }}>
                    <span>Enter address manually</span>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5" /></svg>
                </button>
                <div id={manualAddressId} className="ah-manual-address" hidden={!manualDeliveryEntry}>{manualDeliveryEntry && <>
                    <RequiredField label="Address line 1" hideLabel error={detailErrors.address && manualDeliveryAddress.line1.trim().length < 3 ? detailErrors.address : undefined} attempt={validationAttempt}>
                        <input data-detail-field="address" placeholder="Address line 1" value={manualDeliveryAddress.line1} maxLength={200} onChange={(event) => { setManualDeliveryAddress({ ...manualDeliveryAddress, line1: event.target.value }); clearDetailError("address") }} autoComplete="shipping address-line1" />
                    </RequiredField>
                    <Field label="Address line 2" hideLabel><input placeholder="Address line 2 (optional)" value={manualDeliveryAddress.line2} maxLength={200} onChange={(event) => setManualDeliveryAddress({ ...manualDeliveryAddress, line2: event.target.value })} autoComplete="shipping address-line2" /></Field>
                    <div className="ah-manual-address-locality">
                        <Field label="Postcode" hideLabel><input placeholder="Postcode" value={deliveryPostcode} readOnly autoComplete="shipping postal-code" /></Field>
                        <RequiredField label="City" hideLabel error={detailErrors.address && manualDeliveryAddress.city.trim().length < 2 ? detailErrors.address : undefined} attempt={validationAttempt}>
                            <input data-detail-field="address" placeholder="City" value={manualDeliveryAddress.city} maxLength={100} onChange={(event) => { setManualDeliveryAddress({ ...manualDeliveryAddress, city: event.target.value }); clearDetailError("address") }} autoComplete="shipping address-level2" />
                        </RequiredField>
                    </div>
                </>}</div>
                {addressLookupMessage && <p className="ah-address-status" role="status" aria-live="polite">{addressLookupMessage}</p>}
            </div>
            </fieldset>
        </section>
    )
}
